import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { FaCalculator, FaPlus, FaSearch, FaSpinner, FaTrash } from 'react-icons/fa';

const Api9Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const entries = state.entries || [];
    const inputMethod = state.inputMethod || 'activity';
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    const materialCategories = {
        'Steel (structural, plate, rebar)': {
            units: ['t', 'kg'], examples: ['Steel rebar — 220 t in 2024'],
            searchQuery: 'steel production', preferred_unit_type: 'Mass', defaultUnit: 't',
            supportedMethods: ['activity'] // Activity-based only
        },
        'Cement / Concrete': {
            units: ['t', 'kg', 'm3'], examples: ['Ready-mix concrete C25 — 1,200 m³ in 2024'],
            searchQuery: 'cement production', preferred_unit_type: 'Mass', defaultUnit: 't',
            supportedMethods: ['activity'] // Activity-based only
        },
        'Explosives (ANFO / ammonium nitrate)': {
            units: ['t', 'kg'], examples: ['ANFO — 480 t in 2024'],
            searchQuery: 'ammonium nitrate production', preferred_unit_type: 'Mass', defaultUnit: 't',
            supportedMethods: ['activity'] // Activity-based only
        },
        'Reagents (Sulfuric acid / Sodium cyanide / Lime–quicklime)': {
            units: ['t', 'kg', 'L', 'm3'], examples: ['Sulfuric acid — 1,500 t in 2024'],
            searchQuery: 'chemical production', preferred_unit_type: 'Mass', defaultUnit: 't',
            supportedMethods: ['activity'] // Activity-based only
        },
        'Heavy equipment tires': {
            units: ['units', 't', 'kg'], examples: ['Haul truck tires — 40 units, typical 800 kg each'],
            searchQuery: 'tire manufacturing', preferred_unit_type: 'Mass', defaultUnit: 'units',
            supportedMethods: ['spend'] // Spend-based only
        },
        'Packaging (wood pallets, cardboard)': {
            units: ['t', 'kg', 'units'], examples: ['Wood pallets — 2,000 units, 20 kg each'],
            searchQuery: 'packaging production', preferred_unit_type: 'Mass', defaultUnit: 't',
            supportedMethods: ['spend'] // Spend-based only
        },
        'Lubricants / Oils': {
            units: ['L', 't', 'kg'], examples: ['Hydraulic oil ISO 46 — 25,000 L'],
            searchQuery: 'lubricating oil production', preferred_unit_type: 'Volume', defaultUnit: 'L',
            supportedMethods: ['activity'] // Activity-based only
        },
        'Contract services': {
            units: ['m3', 't', 'kg', 'units'], examples: ['Concrete supplied & poured by contractor — 900 m³'],
            searchQuery: 'construction materials', preferred_unit_type: 'Mass', defaultUnit: 'm3',
            supportedMethods: ['spend'] // Spend-based only
        }
    };

    const countryCodes = ['CL', 'US', 'CN', 'DE', 'JP', 'KR', 'BR', 'CA', 'AU', 'MX', 'PE', 'AR', 'CO', 'Other'];
    const currencies = ['CLP', 'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'CNY', 'JPY', 'MXN', 'Other'];
    const conversionFactors = {
        'concrete': 2400, 'cement': 1500, 'sulfuric_acid': 1840, 'lubricating_oil': 900,
        'haul_truck_tire': 800, 'wood_pallet': 20
    };

    // Get available categories based on current input method
    const getAvailableCategories = () => {
        return Object.keys(materialCategories).filter(category =>
            materialCategories[category].supportedMethods.includes(inputMethod)
        );
    };

    const searchEmissionFactors = async (category, country = 'CL') => {
        try {
            const config = materialCategories[category];
            if (!config) return null;

            // Use direct activity IDs for categories we know work
            const directActivityIds = {
                'Heavy equipment tires': 'plastics_rubber-type_tires',
                'Packaging (wood pallets, cardboard)': 'timber_forestry-type_wood_containers_and_pallets',
                'Lubricants / Oils': 'chemicals-type_lubricating_oil',
                'Contract services': 'real_estate-type_real_estate_services_on_a_fee_or_contract_basis'
            };

            const directActivityId = directActivityIds[category];

            if (directActivityId) {
                // For categories with known working activity IDs, create a direct emission factor object
                const directFactor = {
                    activity_id: directActivityId,
                    data_version: "^0"
                };

                updateState({ availableFactors: { ...state.availableFactors, [category]: directFactor } });
                return directFactor;
            }

            // For other categories, use the existing search logic
            const params = new URLSearchParams({
                query: config.searchQuery,
                data_version: '^3',
                region: country,
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '10'
            });

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) return null;
            const data = await response.json();

            if (data.results && data.results.length > 0) {
                const productionFactors = data.results.filter(factor =>
                    factor.source_lca_activity === 'production' ||
                    factor.activity_id.includes('production') ||
                    factor.name.toLowerCase().includes('production')
                );

                let bestFactor = productionFactors[0] || data.results[0];

                if (config.preferred_unit_type) {
                    const preferredFactor = productionFactors.find(factor =>
                        factor.unit_type === config.preferred_unit_type
                    ) || data.results.find(factor =>
                        factor.unit_type === config.preferred_unit_type
                    );
                    if (preferredFactor) bestFactor = preferredFactor;
                }

                updateState({ availableFactors: { ...state.availableFactors, [category]: bestFactor } });
                return bestFactor;
            }

            return null;
        } catch (error) {
            console.error(`Error searching emission factors for ${category}:`, error);
            return null;
        }
    };

    const handleSearchFactors = async () => {
        if (entries.length === 0) {
            toastr.error('Please add at least one material entry first');
            return;
        }

        updateState({ isSearching: true, availableFactors: {} });
        toastr.info('Searching for material emission factors...');

        try {
            const categoriesToSearch = [...new Set(entries.map(entry => entry.category).filter(Boolean))];
            let foundCount = 0;

            for (const category of categoriesToSearch) {
                const factor = await searchEmissionFactors(category);
                if (factor) foundCount++;
            }

            if (foundCount > 0) {
                toastr.success(`Found emission factors for ${foundCount} material(s)! Click Calculate to proceed.`);
            } else {
                toastr.warning('No specific factors found. Using default calculation method.');
            }
        } catch (error) {
            toastr.error('Search failed. Please check your API key and try again.');
        } finally {
            updateState({ isSearching: false });
        }
    };

    const callClimateIQAPI = async (entry, emissionFactor) => {
        try {
            let requestBody;

            // Check if this is one of our direct activity ID categories
            const directActivityIds = {
                'Heavy equipment tires': 'plastics_rubber-type_tires',
                'Packaging (wood pallets, cardboard)': 'timber_forestry-type_wood_containers_and_pallets',
                'Lubricants / Oils': 'chemicals-type_lubricating_oil',
                'Contract services': 'real_estate-type_real_estate_services_on_a_fee_or_contract_basis'
            };

            const directActivityId = directActivityIds[entry.category];

            if (directActivityId) {
                // Use the EXACT format from your curl commands for direct activity IDs
                if (entry.category === 'Heavy equipment tires') {
                    // EXACT format from your curl command for tires
                    if (inputMethod === 'activity') {
                        // For activity-based, convert to spend-based since the API only accepts money
                        const quantity = parseFloat(entry.quantity);
                        const estimatedCost = quantity * 1000; // Simple conversion
                        requestBody = {
                            emission_factor: {
                                activity_id: directActivityId,
                                data_version: "^0"
                            },
                            parameters: {
                                money: estimatedCost,
                                money_unit: 'usd'
                            }
                        };
                    } else {
                        // Spend-based - EXACT format from curl
                        const spend = parseFloat(entry.annualSpend);
                        requestBody = {
                            emission_factor: {
                                activity_id: directActivityId,
                                data_version: "^0"
                            },
                            parameters: {
                                money: spend,
                                money_unit: entry.currency.toLowerCase()
                            }
                        };
                    }
                }
                else if (entry.category === 'Packaging (wood pallets, cardboard)') {
                    // EXACT format from your curl command for packaging
                    if (inputMethod === 'activity') {
                        const quantity = parseFloat(entry.quantity);
                        const estimatedCost = quantity * 1000; // Simple conversion
                        requestBody = {
                            emission_factor: {
                                activity_id: directActivityId,
                                data_version: "^0"
                            },
                            parameters: {
                                money: estimatedCost,
                                money_unit: 'usd'
                            }
                        };
                    } else {
                        const spend = parseFloat(entry.annualSpend);
                        requestBody = {
                            emission_factor: {
                                activity_id: directActivityId,
                                data_version: "^0"
                            },
                            parameters: {
                                money: spend,
                                money_unit: entry.currency.toLowerCase()
                            }
                        };
                    }
                }
                else if (entry.category === 'Lubricants / Oils') {
                    // EXACT format from your curl command for lubricants
                    if (inputMethod === 'activity') {
                        const quantity = parseFloat(entry.quantity);
                        let weight = quantity;
                        let weightUnit = entry.unit;

                        // Convert to tonnes as in your example (80t)
                        if (entry.unit === 'kg') {
                            weight = weight / 1000;
                            weightUnit = 't';
                        } else if (entry.unit === 'L') {
                            // Convert liters to tonnes using density
                            weight = quantity * (conversionFactors.lubricating_oil / 1000000);
                            weightUnit = 't';
                        }

                        requestBody = {
                            emission_factor: {
                                activity_id: directActivityId,
                                data_version: "^0"
                            },
                            parameters: {
                                weight: weight,
                                weight_unit: weightUnit
                            }
                        };
                    } else {
                        // For spend-based, use money parameters
                        const spend = parseFloat(entry.annualSpend);
                        requestBody = {
                            emission_factor: {
                                activity_id: directActivityId,
                                data_version: "^0"
                            },
                            parameters: {
                                money: spend,
                                money_unit: entry.currency.toLowerCase()
                            }
                        };
                    }
                }
                else if (entry.category === 'Contract services') {
                    // EXACT format from your curl command for contract services
                    if (inputMethod === 'activity') {
                        const quantity = parseFloat(entry.quantity);
                        const estimatedCost = quantity * 1000; // Simple conversion
                        requestBody = {
                            emission_factor: {
                                activity_id: directActivityId,
                                data_version: "^0"
                            },
                            parameters: {
                                money: estimatedCost,
                                money_unit: 'usd'
                            }
                        };
                    } else {
                        const spend = parseFloat(entry.annualSpend);
                        requestBody = {
                            emission_factor: {
                                activity_id: directActivityId,
                                data_version: "^0"
                            },
                            parameters: {
                                money: spend,
                                money_unit: entry.currency.toLowerCase()
                            }
                        };
                    }
                }
            } else {
                // For other categories (Steel, Cement, Explosives, Reagents), use the original logic
                if (inputMethod === 'activity') {
                    let quantity = parseFloat(entry.quantity);
                    let apiUnit = entry.unit;

                    if (emissionFactor.unit_type === 'Mass') {
                        if (entry.unit === 't') {
                            quantity = quantity * 1000;
                            apiUnit = 'kg';
                        } else if (entry.unit === 'm3') {
                            if (entry.category.includes('Concrete')) {
                                quantity = quantity * conversionFactors.concrete;
                            } else if (entry.category.includes('Cement')) {
                                quantity = quantity * conversionFactors.cement;
                            } else if (entry.category.includes('Reagents')) {
                                quantity = quantity * conversionFactors.sulfuric_acid;
                            }
                            apiUnit = 'kg';
                        } else if (entry.unit === 'L') {
                            if (entry.category.includes('Lubricants')) {
                                quantity = quantity * (conversionFactors.lubricating_oil / 1000);
                            }
                            apiUnit = 'kg';
                        } else if (entry.unit === 'units') {
                            if (entry.category.includes('tires')) {
                                quantity = quantity * conversionFactors.haul_truck_tire;
                            } else if (entry.category.includes('Packaging') && entry.category.includes('pallets')) {
                                quantity = quantity * conversionFactors.wood_pallet;
                            }
                            apiUnit = 'kg';
                        }

                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                source: emissionFactor.source,
                                region: emissionFactor.region,
                                year: parseInt(emissionFactor.year),
                                data_version: "^3"
                            },
                            parameters: {
                                mass: quantity,
                                mass_unit: apiUnit
                            }
                        };
                    } else if (emissionFactor.unit_type === 'Volume') {
                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                source: emissionFactor.source,
                                region: emissionFactor.region,
                                year: parseInt(emissionFactor.year),
                                data_version: "^3"
                            },
                            parameters: {
                                volume: quantity,
                                volume_unit: apiUnit
                            }
                        };
                    } else {
                        requestBody = {
                            emission_factor: {
                                activity_id: emissionFactor.activity_id,
                                source: emissionFactor.source,
                                region: emissionFactor.region,
                                year: parseInt(emissionFactor.year),
                                data_version: "^3"
                            },
                            parameters: {
                                weight: quantity,
                                weight_unit: apiUnit
                            }
                        };
                    }
                } else {
                    const spend = parseFloat(entry.annualSpend);
                    requestBody = {
                        emission_factor: {
                            activity_id: emissionFactor.activity_id,
                            source: emissionFactor.source,
                            region: emissionFactor.region,
                            year: parseInt(emissionFactor.year),
                            data_version: "^3"
                        },
                        parameters: {
                            money: spend,
                            money_unit: entry.currency.toLowerCase()
                        }
                    };
                }
            }

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Error calling ClimateIQ API for ${entry.category}:`, error);
            throw error;
        }
    };

    const handleCalculate = async () => {
        if (entries.length === 0) {
            toastr.error('Please add at least one material entry');
            return;
        }

        if (Object.keys(state.availableFactors || {}).length === 0) {
            toastr.error('Please search for emission factors first');
            return;
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating emissions via API...');

        try {
            const results = {};
            let hasSuccessfulCalculation = false;

            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];

                try {
                    const emissionFactor = state.availableFactors?.[entry.category];
                    if (!emissionFactor) {
                        toastr.warning(`No emission factor found for ${entry.category}`);
                        continue;
                    }

                    if (inputMethod === 'activity') {
                        const quantity = parseFloat(entry.quantity);
                        if (!quantity || isNaN(quantity) || quantity <= 0) {
                            toastr.error(`Invalid quantity for ${entry.category}`);
                            continue;
                        }
                    } else {
                        const spend = parseFloat(entry.annualSpend);
                        if (!spend || isNaN(spend) || spend <= 0) {
                            toastr.error(`Invalid spend amount for ${entry.category}`);
                            continue;
                        }
                    }

                    const apiResult = await callClimateIQAPI(entry, emissionFactor);

                    const resultKey = `material_${i}`;

                    results[resultKey] = {
                        category: entry.category,
                        description: entry.description,
                        quantity: inputMethod === 'activity' ? parseFloat(entry.quantity) : parseFloat(entry.annualSpend),
                        quantity_unit: inputMethod === 'activity' ? entry.unit : entry.currency,
                        co2e: apiResult.co2e,
                        co2e_unit: apiResult.co2e_unit,
                        calculation_method: apiResult.co2e_calculation_method,
                        activity_data: apiResult.activity_data,
                        emission_factor: emissionFactor,
                        supplierCountry: entry.supplierCountry,
                        period: entry.period
                    };

                    toastr.success(`Calculated emissions for ${entry.category}: ${apiResult.co2e.toFixed(2)} ${apiResult.co2e_unit}`);
                    hasSuccessfulCalculation = true;
                } catch (error) {
                    console.error(`API error for material entry ${i + 1}:`, error);
                    toastr.error(`Failed to calculate emissions for ${entry.category}: ${error.message}`);
                }
            }

            updateState({ calculationResults: results });

            if (hasSuccessfulCalculation) {
                const totalEmissions = Object.values(results).reduce((sum, result) => sum + (result.co2e || 0), 0);
                toastr.success(`Calculation complete! Total emissions: ${totalEmissions.toFixed(2)} kg CO₂e`);
            } else {
                toastr.warning('No emissions could be calculated. Please check your inputs and API key.');
            }
        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    const validateEntries = () => {
        const errors = [];
        entries.forEach((entry, index) => {
            if (!entry.category) errors.push(`Entry ${index + 1}: Please select a material category`);
            if (!entry.period) errors.push(`Entry ${index + 1}: Please enter a reporting period`);
            if (inputMethod === 'activity') {
                if (!entry.quantity || parseFloat(entry.quantity) <= 0) errors.push(`Entry ${index + 1}: Please enter a valid quantity`);
                if (!entry.unit) errors.push(`Entry ${index + 1}: Please select a unit`);
            } else {
                if (!entry.annualSpend || parseFloat(entry.annualSpend) <= 0) errors.push(`Entry ${index + 1}: Please enter a valid annual spend`);
                if (!entry.currency) errors.push(`Entry ${index + 1}: Please select a currency`);
            }
            if (!entry.supplierCountry) errors.push(`Entry ${index + 1}: Please select a supplier country`);
        });
        return errors;
    };

    const handleAddEntry = () => {
        const newEntries = [...entries, {
            category: '', description: '', quantity: '', unit: '',
            annualSpend: '', currency: 'CLP', supplierCountry: 'CL',
            period: `${formData.year || new Date().getFullYear()} (Jan–Dec)`, evidence: null
        }];
        updateState({ entries: newEntries });
    };

    const handleEntryChange = (index, field, value) => {
        const updated = [...entries];
        updated[index][field] = value;

        if (field === 'category' && value && inputMethod === 'activity') {
            const categoryConfig = materialCategories[value];
            if (categoryConfig) {
                updated[index].unit = categoryConfig.defaultUnit;
            }
        }

        updateState({ entries: updated });
    };

    const handleRemoveEntry = (index) => {
        updateState({ entries: entries.filter((_, i) => i !== index) });
    };

    const loadSampleData = () => {
        // Load sample data based on current input method
        const availableCategories = getAvailableCategories();
        const sampleCategory = availableCategories.length > 0 ? availableCategories[0] : 'Steel (structural, plate, rebar)';

        let sampleEntry;
        if (inputMethod === 'activity') {
            sampleEntry = {
                category: sampleCategory,
                description: `${sampleCategory} sample data`,
                quantity: '100',
                unit: materialCategories[sampleCategory]?.defaultUnit || 't',
                supplierCountry: 'CL',
                period: '2025 (Jan–Dec)',
                evidence: null
            };
        } else {
            sampleEntry = {
                category: sampleCategory,
                description: `${sampleCategory} sample data`,
                annualSpend: '50000',
                currency: 'USD',
                supplierCountry: 'CL',
                period: '2025 (Jan–Dec)',
                evidence: null
            };
        }

        updateState({
            formData: { ...formData, year: '2025' },
            entries: [sampleEntry]
        });
        toastr.info('Sample data loaded. Click "Search Factors" to find emission factors.');
    };

    const totalQuantity = entries.reduce((sum, entry) => sum + (parseFloat(entry.quantity) || 0), 0);
    const totalSpend = entries.reduce((sum, entry) => sum + (parseFloat(entry.annualSpend) || 0), 0);
    const totalEmissions = Object.values(state.calculationResults || {}).reduce((sum, result) => sum + (result.co2e || 0), 0);

    const availableCategories = getAvailableCategories();

    return (
        <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzpa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                    </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    {(entries.length > 0 || Object.keys(state.calculationResults || {}).length > 0) && (
                        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                <h3 className="text-sm font-semibold text-blue-800">Total Materials</h3>
                                <p className="text-2xl font-bold text-blue-600">
                                    {inputMethod === 'activity' ? `${totalQuantity.toLocaleString()} units` : `${totalSpend.toLocaleString()} ${entries[0]?.currency || 'CLP'}`}
                                </p>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                                <h3 className="text-sm font-semibold text-green-800">Total Emissions</h3>
                                <p className="text-2xl font-bold text-green-600">{totalEmissions.toFixed(0)} kg CO₂e</p>
                            </div>
                            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                                <h3 className="text-sm font-semibold text-purple-800">Material Types</h3>
                                <p className="text-2xl font-bold text-purple-600">{entries.length}</p>
                            </div>
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Reporting Year *</label>
                        <select
                            value={formData.year || new Date().getFullYear().toString()}
                            onChange={(e) => updateState({ formData: { ...formData, year: e.target.value } })}
                            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                (new Date().getFullYear() - i).toString()
                            ).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">9.2 Data Input Method</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <button
                                onClick={() => updateState({ inputMethod: 'activity' })}
                                className={`p-6 rounded-lg border-2 transition-all text-left ${inputMethod === 'activity'
                                        ? 'border-green-500 bg-green-50 text-green-700'
                                        : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                <div className="font-semibold text-lg">Activity-based (Preferred)</div>
                                <div className="text-sm mt-2">Provide quantities by material (tonnes, kg, m³, units).</div>
                                <div className="text-xs mt-2 text-green-600">
                                    Available: Steel, Cement, Explosives, Reagents, Lubricants
                                </div>
                            </button>
                            <button
                                onClick={() => updateState({ inputMethod: 'spend' })}
                                className={`p-6 rounded-lg border-2 transition-all text-left ${inputMethod === 'spend'
                                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                                        : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                            >
                                <div className="font-semibold text-lg">Spend-based (Fallback)</div>
                                <div className="text-sm mt-2">Provide annual spend by category.</div>
                                <div className="text-xs mt-2 text-blue-600">
                                    Available: Tires, Packaging, Contract Services
                                </div>
                            </button>
                        </div>
                    </div>

                    <div className="mb-6">
                        <button
                            onClick={handleSearchFactors}
                            disabled={state.isSearching}
                            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isSearching ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaSearch className="w-4 h-4" />
                            )}
                            <span>{state.isSearching ? 'Searching...' : 'Search Emission Factors'}</span>
                        </button>
                    </div>

                    {Object.keys(state.availableFactors || {}).length > 0 && (
                        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h3 className="font-semibold text-green-800 mb-2">Available Material Emission Factors</h3>
                            <div className="space-y-2">
                                {Object.entries(state.availableFactors).map(([category, factor]) => (
                                    <div key={category} className="text-sm text-green-700">
                                        <strong>{category}:</strong> {factor.name} ({factor.source}, {factor.region})
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-700 mb-4">
                            9.2 Key Purchased Materials/Services - {inputMethod === 'activity' ? 'Activity-based' : 'Spend-based'}
                        </h3>

                        <button
                            onClick={handleAddEntry}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 mb-6"
                        >
                            <FaPlus className="w-4 h-4" />
                            <span>Add Material Entry</span>
                        </button>

                        {entries.map((entry, index) => {
                            const resultKey = `material_${index}`;
                            const result = state.calculationResults?.[resultKey];
                            const categoryConfig = materialCategories[entry.category];

                            return (
                                <div key={index} className="p-6 border-2 border-gray-200 rounded-lg space-y-4 mb-4">
                                    <div className="flex justify-between items-center">
                                        <h4 className="font-semibold">Material Entry {index + 1}</h4>
                                        <button
                                            onClick={() => handleRemoveEntry(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FaTrash className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {result && (
                                        <div className="p-3 bg-green-100 rounded-lg border border-green-200">
                                            <p className="text-sm text-green-800 font-medium">
                                                Emissions: {result.co2e.toFixed(2)} {result.co2e_unit}
                                            </p>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Material Category *</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.category}
                                                onChange={(e) => handleEntryChange(index, 'category', e.target.value)}
                                            >
                                                <option value="">Select category</option>
                                                {availableCategories.map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Description/Specification</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., Steel rebar for construction"
                                                value={entry.description}
                                                onChange={(e) => handleEntryChange(index, 'description', e.target.value)}
                                            />
                                        </div>
                                        {inputMethod === 'activity' ? (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity *</label>
                                                    <input
                                                        type="number"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter quantity"
                                                        value={entry.quantity}
                                                        onChange={(e) => handleEntryChange(index, 'quantity', e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit *</label>
                                                    <select
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                        value={entry.unit}
                                                        onChange={(e) => handleEntryChange(index, 'unit', e.target.value)}
                                                    >
                                                        <option value="">Select unit</option>
                                                        {categoryConfig?.units.map(unit => (
                                                            <option key={unit} value={unit}>{unit}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Annual Spend *</label>
                                                    <div className="flex items-center space-x-2">
                                                        <input
                                                            type="number"
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                            placeholder="Enter amount"
                                                            value={entry.annualSpend}
                                                            onChange={(e) => handleEntryChange(index, 'annualSpend', e.target.value)}
                                                        />
                                                        <select
                                                            className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                                                            value={entry.currency}
                                                            onChange={(e) => handleEntryChange(index, 'currency', e.target.value)}
                                                        >
                                                            {currencies.map(curr => (
                                                                <option key={curr} value={curr}>{curr}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Country *</label>
                                            <select
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                value={entry.supplierCountry}
                                                onChange={(e) => handleEntryChange(index, 'supplierCountry', e.target.value)}
                                            >
                                                {countryCodes.map(code => (
                                                    <option key={code} value={code}>{code}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Reporting Period</label>
                                            <input
                                                type="text"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                placeholder="e.g., 2024 (Jan–Dec)"
                                                value={entry.period}
                                                onChange={(e) => handleEntryChange(index, 'period', e.target.value)}
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Upload</label>
                                            <input
                                                type="file"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                onChange={(e) => handleEntryChange(index, 'evidence', e.target.files[0])}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {Object.keys(state.calculationResults || {}).length > 0 && (
                        <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Results Summary</h3>
                            <div className="space-y-3">
                                {Object.entries(state.calculationResults).map(([key, result]) => (
                                    <div key={key} className="flex justify-between items-center p-3 bg-white rounded-lg border border-gray-200">
                                        <div>
                                            <span className="font-medium text-gray-700">
                                                {result.category} - {result.description}
                                            </span>
                                            <p className="text-sm text-gray-500">
                                                {result.quantity} {result.quantity_unit}
                                            </p>
                                        </div>
                                        <span className="font-semibold text-green-700">
                                            {result.co2e.toFixed(2)} {result.co2e_unit}
                                        </span>
                                    </div>
                                ))}
                                <div className="border-t border-gray-300 pt-3 mt-2">
                                    <div className="flex justify-between items-center font-bold text-lg">
                                        <span>Total Purchased Goods Emissions:</span>
                                        <span className="text-blue-700">
                                            {totalEmissions.toFixed(2)} kg CO₂e
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={handleCalculate}
                            disabled={state.isCalculating || Object.keys(state.availableFactors || {}).length === 0}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{state.isCalculating ? 'Calculating...' : 'Calculate'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Api9Form;